import { format, differenceInWeeks } from "date-fns";

export const isSameDay = (date1, date2) => {
  return (
    date1.getDate() === date2.getDate() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getFullYear() === date2.getFullYear()
  );
};

export const startOfWeek = (date = new Date()) => {
  const d = new Date(date);
  // getDay() returns 0 (Sunday) through 6 (Saturday)
  const day = d.getDay();
  // Calculate difference: if it's Sunday (0), subtract 6 days; otherwise, subtract (day - 1)
  const diff = day === 0 ? 6 : day - 1;
  d.setDate(d.getDate() - diff);
  d.setHours(0, 0, 0, 0);
  return d;
};

/**
 * Returns the Monday of the week for the given date.
 * If midDay is true, the returned Date will be set to 12:00 local time;
 * otherwise it will be at 00:00.
 * The original date is not modified.
 */
export const getMondayOfWeek = (date = new Date(), midDay = false) => {
  const monday = startOfWeek(date);
  if (!midDay) {
    return monday;
  }
  const m = new Date(monday);
  m.setHours(12, 0, 0, 0);
  return m;
};

export const getSundayOfWeek = (date = new Date(), midDay = false) => {
  const sunday = endOfWeek(date);
  if (!midDay) {
    return sunday;
  }
  const s = new Date(sunday);
  s.setHours(12, 0, 0, 0);
  return s;
};

export const endOfWeek = (date = new Date()) => {
  // Get Monday of the week using your startOfWeek function
  const monday = startOfWeek(date);
  // Create a new Date from monday to avoid mutating the original object
  const sunday = new Date(monday);
  // Add 6 days to Monday to get Sunday
  sunday.setDate(sunday.getDate() + 6);
  // Set Sunday to the last moment of the day: 23:59:59.999
  sunday.setHours(23, 59, 59, 999);
  return sunday;
};

export const getDateForWeekDay = (weekDay, date = new Date()) => {
  // Get Monday of the current week for the provided date.
  const monday = startOfWeek(date);
  // Create a new Date object so as not to modify monday directly.
  const result = new Date(monday);
  // Add the weekDay offset (0 = Monday, 6 = Sunday)
  result.setDate(monday.getDate() + weekDay);
  return result;
};

// Helper to normalize a Date into a "YYYY-MM-DD" string
export const normalizeDate = (date) => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// Helper to get normalized key from calendar cell properties
export const cellKey = (year, month, day) =>
  `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

export const isSameDayNormalized = (date1, date2) =>
  normalizeDate(date1) === normalizeDate(date2);

export const formatTimestamp = (date, language = "en") => {
  const now = new Date();
  const weekStart = startOfWeek(now);
  const weekEnd = endOfWeek(now);

  // Get hours and minutes with proper formatting
  const hours = date.getHours();
  const minutes =
    date.getMinutes() < 10 ? "0" + date.getMinutes() : date.getMinutes();

  // Define arrays based on language
  const days =
    language === "es"
      ? ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"]
      : ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  const months =
    language === "es"
      ? [
          "Ene",
          "Feb",
          "Mar",
          "Abr",
          "May",
          "Jun",
          "Jul",
          "Ago",
          "Sep",
          "Oct",
          "Nov",
          "Dic",
        ]
      : [
          "Jan",
          "Feb",
          "Mar",
          "Apr",
          "May",
          "Jun",
          "Jul",
          "Aug",
          "Sep",
          "Oct",
          "Nov",
          "Dec",
        ];

  // If the date is today, return only the time
  const isToday =
    date.getDate() === now.getDate() &&
    date.getMonth() === now.getMonth() &&
    date.getFullYear() === now.getFullYear();

  if (isToday) {
    return `${hours}:${minutes}`;
  }

  // If the given date is in the current week, use the weekday abbreviation
  if (date >= weekStart && date <= weekEnd) {
    const dayName = days[date.getDay()];
    return `${dayName}, ${hours}:${minutes}`;
  }

  // Otherwise, format as "Sep 9, 21:07"
  const month = months[date.getMonth()];
  const day = date.getDate();
  return `${month} ${day}, ${hours}:${minutes}`;
};

/**
 * Returns the full day name in uppercase for a given date.
 * @param {Date} date - The date object.
 * @param {string} language - "en" or "es". Defaults to "en".
 * @returns {string} The day of the week in uppercase (e.g., "MONDAY" or "LUNES").
 */
export const getDayOfWeekName = (date, language = "en") => {
  const dayIndex = date.getDay(); // 0 (Sunday) to 6 (Saturday)
  const daysEn = [
    "SUNDAY",
    "MONDAY",
    "TUESDAY",
    "WEDNESDAY",
    "THURSDAY",
    "FRIDAY",
    "SATURDAY",
  ];
  const daysEs = [
    "DOMINGO",
    "LUNES",
    "MARTES",
    "MIÉRCOLES",
    "JUEVES",
    "VIERNES",
    "SÁBADO",
  ];
  return language === "es" ? daysEs[dayIndex] : daysEn[dayIndex];
};

/**
 * Formats a date as day and abbreviated month (e.g., "19 Dec" or "19 Dic").
 * @param {Date} date - The date object.
 * @param {string} language - "en" or "es". Defaults to "en".
 * @returns {string} The formatted date string without year.
 */
export const formatDayMonth = (date, language = "en") => {
  const day = date.getDate();
  const monthsEn = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const monthsEs = [
    "Ene",
    "Feb",
    "Mar",
    "Abr",
    "May",
    "Jun",
    "Jul",
    "Ago",
    "Sep",
    "Oct",
    "Nov",
    "Dic",
  ];
  const monthNames = language === "es" ? monthsEs : monthsEn;
  const month = monthNames[date.getMonth()];
  return `${day} ${month}`;
};

/**
 * Formats a date range as "startDay-endDay Month" if within the same month,
 * or "startDay Month - endDay Month" if spanning different months.
 * @param {Date} startDate - The start date object.
 * @param {Date} endDate - The end date object.
 * @param {string} language - "en" or "es". Defaults to "en".
 * @returns {string} The formatted date range without year.
 */
export const formatDateRange = (startDate, endDate, language = "en") => {
  const dayStart = startDate.getDate();
  const dayEnd = endDate.getDate();
  const monthsEn = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const monthsEs = [
    "Ene",
    "Feb",
    "Mar",
    "Abr",
    "May",
    "Jun",
    "Jul",
    "Ago",
    "Sep",
    "Oct",
    "Nov",
    "Dic",
  ];
  const monthNames = language === "es" ? monthsEs : monthsEn;
  const monthStart = monthNames[startDate.getMonth()];
  const monthEnd = monthNames[endDate.getMonth()];
  // Always use "DD Mon - DD Mon" format for consistency
  return `${dayStart} ${monthStart} - ${dayEnd} ${monthEnd}`;
};

export const getWeekNumber = (date) => {
  if (!date) return null;
  const d = new Date(date);
  if (isNaN(d)) return null;

  const DAY_MS = 86400000;

  // Build a UTC midnight for the local calendar date to avoid DST issues
  const dUTC = Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
  const dowUTC = new Date(dUTC).getUTCDay(); // 0=Sun..6=Sat (UTC)

  // Monday-based index: Mon=0..Sun=6
  const monIndex = (dowUTC + 6) % 7;

  // Monday (00:00 UTC) of the given date's week
  const weekStartUTC = dUTC - monIndex * DAY_MS;

  // Monday (00:00 UTC) of the week that contains Jan 1 of the same year
  const jan1UTC = Date.UTC(d.getFullYear(), 0, 1);
  const jan1DowUTC = new Date(jan1UTC).getUTCDay();
  const jan1MonIndex = (jan1DowUTC + 6) % 7;
  const firstWeekStartUTC = jan1UTC - jan1MonIndex * DAY_MS; // may be in previous Dec — that's fine

  // Week number where Week 1 = week containing Jan 1, weeks run Mon..Sun
  const weekNo =
    Math.floor((weekStartUTC - firstWeekStartUTC) / (7 * DAY_MS)) + 1;
  return weekNo;
};

/**
 * Formats a date intelligently based on how recent it is:
 * - Today: "Today" or "Hoy"
 * - Yesterday: "Yesterday" or "Ayer"
 * - This week (but not today): Day name like "Monday" or "Lunes"
 * - Older than one week: Day and month like "19 Dec" or "19 Dic"
 * @param {Date} date - The date object to format
 * @param {string} language - "en" or "es". Defaults to "en"
 * @returns {string} The formatted date string
 */
export const formatChatDate = (date, language = "en") => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const inputDate = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate()
  );

  // Calculate difference in days
  const timeDiff = today.getTime() - inputDate.getTime();
  const daysDiff = Math.floor(timeDiff / (1000 * 3600 * 24));

  // Today
  if (daysDiff === 0) {
    return language === "es" ? "Hoy" : "Today";
  }

  // Yesterday
  if (daysDiff === 1) {
    return language === "es" ? "Ayer" : "Yesterday";
  }

  // Less than 7 days ago (this week)
  if (daysDiff > 1 && daysDiff < 7) {
    const dayIndex = date.getDay(); // 0 (Sunday) to 6 (Saturday)
    const daysEn = [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ];
    const daysEs = [
      "Domingo",
      "Lunes",
      "Martes",
      "Miércoles",
      "Jueves",
      "Viernes",
      "Sábado",
    ];
    return language === "es" ? daysEs[dayIndex] : daysEn[dayIndex];
  }

  // Older than one week - use formatDayMonth
  return formatDayMonth(date, language);
};
